import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Column from '~/Common-Objects/Grids/Column/Column'
import CopyPasteIcon from '~/Common-Objects/UI/Forms/CopyPasteIcon/copyPasteIcon'
import DataTable1 from '~/Common-Objects/Tables/DataTable1/DataTable1'
import Hint from '~/Common-Objects/UI/Hint/Hint'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import OptionsDropdown from '~/Common-Objects/UI/OptionsDropdown/OptionsDropdown'
import PagingBlock from '~/Common-Objects/Tables/DataTablePaging/DataTablePaging'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Row from '~/Common-Objects/Grids/Row/Row'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import UpsellModal from '~/Common-Objects/UpsellModal'
import axios from '~/data/http/axios'
import i18n from '~/i18n'
import PermissionChecker from '~/Classes/permissions'
import DateTimeFunctions from '~/Classes/dateTimeFunctions'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import SiteVars from '~/Classes/siteVars'
import { Link, useHistory } from 'react-router-dom'

import * as siteMaster from '~/data/actions/siteActions'
import DuplicateLandingPage from './landingPage/duplicate'
import DestroyLandingPage from './landingPage/destroy'
import LandingPagesEmptyState from '~/Common-Objects/LandingPages/EmptyState'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'

export default function LandingPages() {
    const site = useSelector((state) => state.siteMaster)
    const permissionStore = useSelector((state) => state.permission)
    const accountMaster = useSelector((state) => state.accountMaster)
    const dispatch = useDispatch()
    const history = useHistory()

    const timer = useRef()
    const firstLoad = useRef(true)

    const [hasLandingPages, setHasLandingPages] = useState(null)
    const [landingPages, setLandingPages] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingData, setLoadingData] = useState(true)
    const [totalLandingPages, setTotalLandingPages] = useState(-1)

    const [landingPagesTableData, setLandingPagesTableData] = useState([])

    const [sortColumn, setSortColumn] = useState('createdAt')
    const [sortDirection, setSortDirection] = useState('desc')

    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)

    const [searchText, setSearchText] = useState('')
    const [status, setStatus] = useState(-1)
    const [togglingIds, setTogglingIds] = useState([])
    const canReadSubs = PermissionChecker('subscribers', permissionStore.permissions, 'read')
    const canWrite = PermissionChecker('forms', permissionStore.permissions, 'write')
    const { requiredTier } = useTierAllowed('landingPages')

    useEffect(() => {
        getLandingPages()
        return function () {
            if (timer.current) clearTimeout(timer.current)
        }
    }, [])

    useEffect(() => {
        generateTableData(landingPages)
    }, [landingPages, togglingIds])

    useEffect(() => {
        if (firstLoad.current) return
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => getLandingPages(), 1000)
    }, [searchText])

    useEffect(() => {
        if (firstLoad.current) return
        getLandingPages()
    }, [status, pageSize, currentPage, sortColumn, sortDirection])

    const getLandingPages = async () => {
        try {
            setLoadingData(true)
            let q =
                '?limit=' +
                pageSize +
                '&offset=' +
                currentPage * pageSize +
                '&orderBy=' +
                sortColumn +
                '&orderDir=' +
                sortDirection
            if (searchText) q += '&searchText=' + searchText
            if (status !== -1) q += '&status=' + status
            const res = await axios.get('/landingpage' + q)
            if (hasLandingPages === null) setHasLandingPages(!!res.data.total)
            setTotalLandingPages(res.data.total)
            setLandingPages(res.data.landingPages)
            firstLoad.current = false
        } catch (err) {
            console.log(err)
            setLandingPages([])
            setLoading(false)
            setLoadingData(false)
        }
    }

    const generateTableData = () => {
        let landingPagesTblData = []

        landingPages.forEach((row) => {
            const isLive = !!row.status
            const isToggling = togglingIds.includes(row.id)
            const statusValue = canWrite ? (
                <div className='quickFlex' style={{ justifyContent: 'center', alignItems: 'center' }}>
                    <Select
                        buttonClass={isLive ? 'button-green-outline' : 'button-grey-outline'}
                        overrideButtonClass={true}
                        buttonStyle={{ borderRadius: '20px' }}
                        fixedHeader={isLive ? i18n.t('form:landingPages.live') : i18n.t('form:landingPages.draft')}
                        data={[
                            { label: i18n.t('form:landingPages.draft'), value: 0 },
                            { label: i18n.t('form:landingPages.live'), value: 1 }
                        ]}
                        disabled={isToggling}
                        onChange={(e) => toggleStatus(row, e.target.value)}
                        postHogTag='landing-page-list-status'
                        usePortal
                    />
                </div>
            ) : (
                <SquareChip className={isLive ? 'square-chip-green small-icon' : 'small-icon'}>
                    {isLive ? i18n.t('form:landingPages.live') : i18n.t('form:landingPages.draft')}
                </SquareChip>
            )

            const screenshotStamp = new Date(row.lastUpdatedAt || row.updatedAt || row.createdAt).getTime()
            const screenshotUrl =
                SiteVars.repoEndPoint +
                'userContent/' +
                accountMaster.accountMaster.id +
                '/templates/screenshots/landingPage' +
                row.id +
                '.jpg?rnd=' +
                screenshotStamp

            var obj = {
                preview: {
                    headerValue: i18n.t('form:landingPages.preview'),
                    value: (
                        <img
                            alt={row.formName}
                            title={row.formName}
                            style={{ width: '100px', maxHeight: '45px', objectFit: 'cover' }}
                            src={screenshotUrl}
                        />
                    ),
                    orderBy: false,
                    width: 100
                },
                formName: {
                    orderBy: true,
                    headerValue: i18n.t('form:landingPages.landingPageTitle'),
                    value: (
                        <Link
                            to={
                                row.status
                                    ? `/cp/landingpages/${row.id}`
                                    : `/cp/templates/add/dragDropv2/?landingPageId=${row.id}`
                            }
                            style={{ fontWeight: 'bold', textDecoration: 'none' }}
                        >
                            {row.formName}
                        </Link>
                    )
                },
                url: {
                    headerValue: 'URL',
                    value: (
                        <div className='quickFlex' style={{ gap: 5, alignItems: 'center' }}>
                            <div>{row.url}</div>
                            <CopyPasteIcon
                                value={row.url}
                                showOnHover={true}
                                postHogTag='landing-page-list-copy-url'
                                // Each row renders its own tooltip, so the id has to be
                                // unique or every row would share the first one.
                                toolTipId={`landing-page-list-url-tooltip-${row.id}`}
                            />
                        </div>
                    )
                },
                status: {
                    orderBy: true,
                    headerValue: i18n.t('form:landingPages.status'),
                    value: statusValue,
                    width: 160,
                    align: 'center'
                },
                impressions: {
                    orderBy: true,
                    headerValue: i18n.t('form:landingPages.impressions'),
                    value: (
                        <SquareChip className='small-icon'>
                            {FormatNumberFuncs.formatNumber(row.impressions)}
                        </SquareChip>
                    ),
                    width: 150
                },
                lastUpdatedAt: {
                    orderBy: true,
                    headerValue: i18n.t('form:landingPages.lastUpdated'),
                    value: DateTimeFunctions.formatDateTime(row.lastUpdatedAt || row.updatedAt),
                    width: 150
                },
                createdAt: {
                    orderBy: true,
                    headerValue: i18n.t('form:landingPages.created'),
                    value: DateTimeFunctions.formatDateTime(row.createdAt),
                    width: 150
                },
                optionsCol: {
                    headerValue: ' ',
                    value: (
                        <OptionsDropdown popWidth='150px'>
                            <ul>
                                {canReadSubs && (
                                    <li>
                                        <a onClick={() => history.push('/cp/landingpages/' + row.id)}>
                                            {i18n.t('form:index.view')}
                                        </a>
                                    </li>
                                )}
                                {canWrite == true && canReadSubs && (
                                    <li>
                                        <a
                                            onClick={() => {
                                                if (row.status) {
                                                    history.push(`/cp/landingpages/${row.id}`)
                                                } else {
                                                    history.push(
                                                        `/cp/templates/add/dragDropv2/?landingPageId=${row.id}`
                                                    )
                                                }
                                            }}
                                        >
                                            {i18n.t('form:index.edit')}
                                        </a>
                                    </li>
                                )}
                                {canWrite == true && (
                                    <li>
                                        <a onClick={() => duplicate(row)}>{i18n.t('form:index.duplicate')}</a>
                                    </li>
                                )}
                                {canWrite == true && (
                                    <li>
                                        <a onClick={() => deletePage(row)}>{i18n.t('form:index.delete')}</a>
                                    </li>
                                )}
                            </ul>
                        </OptionsDropdown>
                    ),
                    orderBy: false,
                    fixed: true,
                    width: 20
                }
            }
            landingPagesTblData.push(obj)
        })

        setLandingPagesTableData(landingPagesTblData)
        if (loading) setLoading(false)
        setLoadingData(false)
    }

    const addLandingPage = () => {
        history.push('/cp/landingpages/add')
    }

    const duplicate = (row) => {
        const drawer = (
            <DuplicateLandingPage
                landingPage={row}
                onFinish={(newFormId) => {
                    history.push(`/cp/templates/add/dragDropv2/?landingPageId=${newFormId}`)
                }}
            />
        )
        dispatch(siteMaster.alterSiteDrawer(true, true, 'right', drawer, true, 400))
    }

    const toggleStatus = async (row, nextStatus) => {
        if (togglingIds.includes(row.id)) return
        const newStatus = Number(nextStatus)
        if (Number(row.status) === newStatus) return

        if (newStatus === 1) {
            try {
                const tierRes = await axios.get('/accountMaster/tier/landingPages')
                if (!tierRes.data.allowed) {
                    dispatch(
                        siteMaster.alterFullpage(
                            <UpsellModal
                                feature='landingPages'
                                requiredTier={requiredTier?.name || 'Starter'}
                                onUpgrade={() => dispatch(siteMaster.alterSiteDrawer(false, true, 'right', null))}
                            />
                        )
                    )
                    return
                }
            } catch (err) {
                console.log(err)
            }
        }

        setTogglingIds((ids) => [...ids, row.id])
        setLandingPages((pages) => pages.map((page) => (page.id === row.id ? { ...page, status: newStatus } : page)))
        try {
            await axios.put(`/landingpage/${row.id}/status`, { status: newStatus })
            dispatch(
                siteMaster.alterSnackbar(
                    true,
                    newStatus ? i18n.t('form:landingPages.published') : i18n.t('form:landingPages.unpublished')
                )
            )
            await getLandingPages()
        } catch (err) {
            setLandingPages((pages) =>
                pages.map((page) => (page.id === row.id ? { ...page, status: row.status } : page))
            )
            if (err?.data?.error === 'tierOverLimit' || err?.data?.message === 'tierOverLimit') {
                dispatch(
                    siteMaster.alterFullpage(
                        <UpsellModal
                            feature='landingPages'
                            requiredTier={requiredTier?.name || 'Starter'}
                            onUpgrade={() => dispatch(siteMaster.alterSiteDrawer(false, true, 'right', null))}
                        />
                    )
                )
            } else {
                dispatch(siteMaster.alterSnackbar(true, i18n.t('form:landingPages.statusUpdateError')))
            }
            console.log(err)
        } finally {
            setTogglingIds((ids) => ids.filter((id) => id !== row.id))
        }
    }

    const deletePage = (row) => {
        const drawer = (
            <DestroyLandingPage
                key={row.id}
                landingPage={row}
                onFinish={() => {
                    getLandingPages()
                }}
            />
        )
        dispatch(siteMaster.alterSiteDrawer(true, true, 'right', drawer, true, 400))
    }

    if (loading || totalLandingPages === -1) return <LoadingIcons />

    return (
        <>
            <Paper>
                <div className='mar-b25 quickFlex' style={{ justifyContent: 'space-between' }}>
                    <div className='quickFlex m-mar-b10'>
                        <div className='page-header'>{i18n.t('form:landingPages.landingPages')}</div>
                        {site.siteId === 1 && (
                            <Hint
                                href='https://transpond.io/support/growing-your-audience/what-is-a-landing-page'
                                iconLeft={<Icons icon='questionCircle' />}
                            >
                                {i18n.t('form:landingPages.helpLinkText')}
                            </Hint>
                        )}
                    </div>
                    {PermissionChecker('campaigns', permissionStore.permissions, 'write') && (
                        <Button
                            className='button-primary'
                            onClick={addLandingPage}
                            text={i18n.t('form:landingPages.addLandingPage')}
                            iconLeft={<Icons icon='plus' />}
                            postHogTag='landing-page-page-header-create'
                        />
                    )}
                </div>

                {hasLandingPages > 0 && (
                    <>
                        <Row>
                            <Column lg={6} md={6} sm={6} xs={12} className='mar-b25'>
                                <TextInput
                                    placeholder={i18n.t('form:landingPages.searchPlaceholder')}
                                    onChange={(event) => {
                                        setSearchText(event.target.value)
                                    }}
                                    value={searchText}
                                    iconLeft={
                                        <Icons
                                            icon='magnifier'
                                            style={{ height: 16, width: 16, marginTop: '1px' }}
                                            color='#2B2F41'
                                            onClick={() => {}}
                                        />
                                    }
                                />
                            </Column>
                        </Row>
                        <div className='quickFlex' style={{ justifyContent: 'space-between' }}>
                            <div>
                                <div className='quickFlex' style={{ gap: 8 }}>
                                    <Select
                                        inlineLabel='Status'
                                        value={status}
                                        data={[
                                            { label: 'All', value: -1 },
                                            { label: i18n.t('form:landingPages.live'), value: 1 },
                                            { label: i18n.t('form:landingPages.draft'), value: 0 }
                                        ]}
                                        onChange={(e) => setStatus(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <Select
                                    align='right'
                                    inlineLabel='Per Page'
                                    value={pageSize}
                                    data={[10, 30, 50, 100]}
                                    onChange={(e) => setPageSize(e.target.value)}
                                />
                            </div>
                        </div>
                        <DataTable1
                            className='with-image'
                            noResultsTxt={<span>{i18n.t('form:landingPages.noLandingPagesFound')}</span>}
                            tableData={landingPagesTableData}
                            loadingData={loadingData}
                            dataUpdater={(_, orderDir, orderBy) => {
                                setSortColumn(orderBy)
                                setSortDirection(orderDir)
                            }}
                            width='100%'
                            pageSize='100'
                            hasCheckBoxes='no'
                            sortedColumn={sortColumn}
                            sortedDirection={sortDirection}
                        />
                        <Row>
                            <Column xs={12} hide={['xs', 'sm']} className='center-xs'>
                                <PagingBlock
                                    className='mar-t25'
                                    totalRows={totalLandingPages}
                                    pageSize={pageSize}
                                    numberOfLinks='10'
                                    currentPage={currentPage}
                                    changePage={(p) => setCurrentPage(p)}
                                    text={i18n.t('page') + ':'}
                                />
                            </Column>
                        </Row>
                    </>
                )}

                {!hasLandingPages && canWrite && <LandingPagesEmptyState addLandingPage={addLandingPage} />}
            </Paper>
        </>
    )
}
